/**
 * One stage rail and one "needs engineering" rule for every incident.
 * Driven by the AI analysis and the runbook's real progress — never by the
 * mission type or by any integration-specific flow.
 */
import type { AlertAnalysis } from './alertAnalysis';
import type { MissionHeaderStage, MissionHeaderStageStatus } from './missionHeaderStages';
import type { Runbook } from '../services/runbook';
import type { EiInvestigationRun } from '../types/engineeringIntelligence';

/** A code change is needed when the analysis says so or the investigation produced a patch. */
export function incidentNeedsEngineering(
  analysis: AlertAnalysis | null | undefined,
  run: EiInvestigationRun | null | undefined,
): boolean {
  const patchFiles = run?.implementation_package?.engineering_patch?.files?.length ?? 0;
  return analysis?.code_change === 'required' || patchFiles > 0;
}

/**
 * Whether the Engineering tab applies to an incident: it is for code work —
 * a required change, a produced patch, or an operator escalation. Unanalysed
 * incidents keep it until the analysis says otherwise.
 */
export function engineeringTabApplies(
  analysis: AlertAnalysis | null | undefined,
  run: EiInvestigationRun | null | undefined,
  escalated: boolean,
): boolean {
  return !analysis || escalated || incidentNeedsEngineering(analysis, run);
}

/** The two signals disagree: worth telling the operator rather than picking one silently. */
export function engineeringSignalsDisagree(
  analysis: AlertAnalysis | null | undefined,
  run: EiInvestigationRun | null | undefined,
): boolean {
  const patchFiles = run?.implementation_package?.engineering_patch?.files?.length ?? 0;
  return analysis?.code_change === 'not_required' && patchFiles > 0;
}

const finished = (s: { status: string }) => s.status === 'done' || s.status === 'skipped';

function phaseStatus(steps: Runbook['steps'], started: boolean): MissionHeaderStageStatus {
  if (steps.length === 0) return started ? 'completed' : 'pending';
  if (steps.every(finished)) return 'completed';
  if (steps.some((s) => s.status === 'failed' || s.status === 'needs_approval' || s.status === 'needs_input')) return 'blocked';
  return started ? 'active' : 'pending';
}

export function buildIncidentStages(input: {
  analysis: AlertAnalysis | null | undefined;
  runbook: Runbook | null | undefined;
  closed: boolean;
}): MissionHeaderStage[] {
  const { analysis, runbook, closed } = input;
  const fix = runbook?.steps.filter((s) => s.phase !== 'verify') ?? [];
  const verify = runbook?.steps.filter((s) => s.phase === 'verify') ?? [];
  const investigation: MissionHeaderStageStatus = analysis ? 'completed' : closed ? 'completed' : 'active';
  const plan: MissionHeaderStageStatus = runbook ? 'completed' : closed ? 'completed' : analysis ? 'active' : 'pending';
  let execute = runbook ? phaseStatus(fix, true) : closed ? 'completed' : 'pending';
  let check = runbook ? phaseStatus(verify, execute === 'completed') : closed ? 'completed' : 'pending';
  if (closed) {
    // Closing with unfinished steps is allowed; the rail must not claim they ran.
    if (execute !== 'completed') execute = 'pending';
    if (check !== 'completed') check = 'pending';
  }
  return [
    { id: 'entry', label: 'Incident', status: 'completed' },
    { id: 'investigation', label: 'Investigation', status: investigation },
    { id: 'engineering', label: 'Plan', status: plan },
    { id: 'execution', label: 'Execute', status: execute },
    { id: 'testing', label: 'Verify', status: check },
    { id: 'learning', label: 'Closed', status: closed ? 'completed' : check === 'completed' ? 'active' : 'pending' },
  ];
}

/** One sentence for the header: what needs a person, else what is happening. */
export function incidentStageSentence(runbook: Runbook | null | undefined, closed: boolean): string {
  if (closed) return 'The incident is closed.';
  if (!runbook) return 'Building the runbook from the analysis.';
  const failed = runbook.steps.find((s) => s.status === 'failed');
  if (failed) return `Step failed: ${failed.title}`;
  const approval = runbook.steps.find((s) => s.status === 'needs_approval');
  if (approval) return `Approve: ${approval.title}`;
  const input = runbook.steps.find((s) => s.status === 'needs_input');
  if (input) return `Provide input: ${input.title}`;
  const manual = runbook.steps.find((s) => s.kind === 'manual' && !finished(s));
  if (manual) return `Next: ${manual.title}`;
  if (runbook.steps.every(finished)) return 'All runbook steps are finished — mark the incident resolved.';
  return 'Running checks.';
}

/** True when the next move belongs to a person. */
export function incidentNeedsYou(runbook: Runbook | null | undefined): boolean {
  return Boolean(
    runbook?.steps.some(
      (s) => s.status === 'failed' || s.status === 'needs_approval' || s.status === 'needs_input' || (s.kind === 'manual' && !finished(s)),
    ),
  );
}

/** The first runbook step that still needs a person. Presentation only — same predicate as incidentNeedsYou. */
export function pendingOperatorStep(runbook: Runbook | null | undefined): Runbook['steps'][number] | null {
  return (
    runbook?.steps.find(
      (s) => s.status === 'failed' || s.status === 'needs_approval' || s.status === 'needs_input' || (s.kind === 'manual' && !finished(s)),
    ) ?? null
  );
}
